import { ColumnDef } from "@tanstack/react-table";
import { Fragment, ReactNode } from "react";
import {
  CustomerUpdate,
  CustomerUpdateStatus,
  CustomerUpdateStatusLabels,
  Employee,
  TerminologyOptions,
} from "@/types/v2";
import { HeaderSortable } from "@/components/Base/HeaderSortable";
import { cn, firestoreToDateTime } from "@/lib/utils";
import { formatEmployeeName } from "@/lib/employees";
import { Header } from "@/components/Base/Header";
import { differenceInMinutes, format } from "date-fns";
import { Icon } from "@/components/Common/Icon";
import { getStatusIcon, statusClasses } from "@/lib/customerUpdates";
import { TableLayoutState } from "@/components/Base/Datatable";
import { ExternalLinkWrapper } from "@/components/Dashboard/components/Card/ExternalLinkWrapper";
import { Badge } from "@/components/Base/Badge";

export type DatatableCustomerUpdate = CustomerUpdate;

interface ColumnProps {
  actions: (customerUpdate: DatatableCustomerUpdate) => ReactNode[];
  employees?: Employee[];
  currentStatus?: CustomerUpdateStatus | "all" | undefined;
  terminolize?: (
    term: string,
    option: TerminologyOptions,
    form?: "singular" | "plural" | "acronym"
  ) => string;
}

const createActionsColumn = ({
  actions,
}: ColumnProps): ColumnDef<DatatableCustomerUpdate> => ({
  accessorKey: "actions",
  header: "",
  cell: ({ row }) => {
    const item = row.original;
    return (
      <div className="flex gap-2">
        {actions(item).map((action, index) => (
          <Fragment key={`action-${item.id}-${index}`}>{action}</Fragment>
        ))}
      </div>
    );
  },
});

// Helper function to format status with appropriate styling
const formatStatus = (status: CustomerUpdateStatus, pastTime?: boolean) => {
  const pastTimeClasses = () => {
    if (pastTime === undefined) return "";
    return pastTime ? "bg-red text-white hover:bg-red/80" : "";
  };

  return (
    <Badge
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium",
        statusClasses[status],
        pastTimeClasses()
      )}
    >
      <Icon name={getStatusIcon(status)} className="mr-2" size={14} />
      {pastTime ? "PAST DUE" : CustomerUpdateStatusLabels[status]}
    </Badge>
  );
};

// Helper function to determine if a column should be sortable based on status
const isColumnSortable = (
  columnId: string,
  status?: CustomerUpdateStatus | "all" | undefined
): boolean => {
  switch (columnId) {
    case "completedAt":
      return status === CustomerUpdateStatus.COMPLETED;
    case "completedBy":
      return status === CustomerUpdateStatus.COMPLETED;
    case "scheduledUpdateTime":
      return status !== CustomerUpdateStatus.CREATED;
    case "cancelledAt":
      return status === CustomerUpdateStatus.CANCELLED;
    case "skippedAt":
      return status === CustomerUpdateStatus.SKIPPED;
    default:
      return true; // Most columns are always sortable
  }
};

const customerUpdateColumns = (props?: ColumnProps) => {
  const { currentStatus, terminolize = (term: string) => term } = props || {};

  const col: ColumnDef<DatatableCustomerUpdate>[] = [
    {
      accessorKey: "id",
      meta: {
        label: "ID",
      },
      header: () => <Header title={"ID"} />,
    },
    {
      accessorKey: "status",
      header: ({ column }) =>
        isColumnSortable("status", currentStatus) ? (
          <HeaderSortable column={column} title={"Status"} />
        ) : (
          <Header title={"Status"} />
        ),
      cell: ({ row }) => {
        const item = row.original;
        // Determine if the update is past due
        const timeSinceScheduled = differenceInMinutes(
          new Date(),
          firestoreToDateTime(item.actualUpdateTime) as Date
        );
        const pastTime =
          item.status !== CustomerUpdateStatus.PENDING
            ? undefined
            : item.status === CustomerUpdateStatus.PENDING &&
              item.scheduledUpdateTime &&
              timeSinceScheduled > item.completedOnTimeBuffer;

        return formatStatus(item.status, pastTime);
      },
    },
    {
      accessorKey: "cardNumber",
      meta: {
        label: terminolize(
          "RO Number",
          TerminologyOptions.repairOrderNumber,
          "acronym"
        ),
      },
      header: ({ column }) =>
        isColumnSortable("cardNumber", currentStatus) ? (
          <HeaderSortable
            column={column}
            title={terminolize("RO", TerminologyOptions.repairOrder, "acronym")}
          />
        ) : (
          <Header
            title={terminolize("RO", TerminologyOptions.repairOrder, "acronym")}
          />
        ),
      cell: ({ row }) => {
        const cardNumber = row.original.cardNumber ?? "";

        return (
          <ExternalLinkWrapper
            cardId={
              row.original.cardSource && row.original.cardSource !== "manual"
                ? row.original.cardId
                : undefined
            }
          >
            <span
              className={cn(
                row.original.cardSource && row.original.cardSource !== "manual"
                  ? "cursor-pointer hover:underline"
                  : ""
              )}
            >
              {cardNumber}
            </span>
          </ExternalLinkWrapper>
        );
      },
    },
    {
      accessorKey: "customerDisplayName",
      meta: {
        label: "Customer Name",
      },
      header: ({ column }) =>
        isColumnSortable("customerDisplayName", currentStatus) ? (
          <HeaderSortable column={column} title={"Customer Name"} />
        ) : (
          <Header title={"Customer Name"} />
        ),
    },
    {
      accessorKey: "vehicle",
      meta: {
        label: terminolize("Vehicle", TerminologyOptions.vehicle, "singular"),
      },
      header: ({ column }) =>
        isColumnSortable("vehicle", currentStatus) ? (
          <HeaderSortable
            column={column}
            title={terminolize(
              "Vehicle",
              TerminologyOptions.vehicle,
              "singular"
            )}
          />
        ) : (
          <Header
            title={terminolize(
              "Vehicle",
              TerminologyOptions.vehicle,
              "singular"
            )}
          />
        ),
    },
    {
      accessorKey: "comments",
      header: () => <Header title={"Comments"} />,
      cell: ({ row }) => {
        const item = row.original;
        return <div className="text-sm">{item.comments}</div>;
      },
    },
    {
      accessorKey: "desiredUpdateTime",
      meta: {
        label: "Desired Update Time",
      },
      header: () => <Header title={"Desired Update Time"} />,
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="text-sm">
            {item.desiredUpdateTime
              ? format(
                  firestoreToDateTime(item.desiredUpdateTime) as Date,
                  "Pp"
                )
              : "Not set"}
          </div>
        );
      },
    },
    {
      accessorKey: "scheduledUpdateTime",
      meta: {
        label: "Scheduled Update Time",
      },
      header: ({ column }) =>
        isColumnSortable("scheduledUpdateTime", currentStatus) ? (
          <HeaderSortable column={column} title={"Scheduled Update Time"} />
        ) : (
          <Header title={"Scheduled Update Time"} />
        ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="text-sm">
            {item.scheduledUpdateTime
              ? format(
                  firestoreToDateTime(item.scheduledUpdateTime) as Date,
                  "Pp"
                )
              : "Not scheduled"}
          </div>
        );
      },
    },
    {
      accessorKey: "userId",
      meta: {
        label: "Assigned User",
      },
      header: ({ column }) =>
        isColumnSortable("userId", currentStatus) ? (
          <HeaderSortable column={column} title={"Assigned User"} />
        ) : (
          <Header title={"Assigned User"} />
        ),
      cell: ({ row }) => {
        const item = row.original;
        const employee = props?.employees?.find(
          (e) => e.shopHeroAuthUid === item.userId
        );
        return <div className="text-sm">{formatEmployeeName(employee)}</div>;
      },
    },
    {
      accessorKey: "completedBy",
      meta: {
        label: "Completed By",
      },
      header: ({ column }) =>
        isColumnSortable("completedBy", currentStatus) ? (
          <HeaderSortable column={column} title={"Completed By"} />
        ) : (
          <Header title={"Completed By"} />
        ),
      cell: ({ row }) => {
        const item = row.original;
        const employee = props?.employees?.find(
          (e) => e.shopHeroAuthUid === item.completedBy
        );
        if (!item.completedBy) return null;
        return <div className="text-sm">{formatEmployeeName(employee)}</div>;
      },
    },
    {
      accessorKey: "completedAt",
      meta: {
        label: "Completed At",
      },
      header: ({ column }) =>
        isColumnSortable("completedAt", currentStatus) ? (
          <HeaderSortable column={column} title={"Completed At"} />
        ) : (
          <Header title={"Completed At"} />
        ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="text-sm">
            {item.completedAt
              ? format(firestoreToDateTime(item.completedAt) as Date, "Pp")
              : "Not completed"}
          </div>
        );
      },
    },
    {
      accessorKey: "completedOnTime",
      meta: {
        label: "Completed On Time",
      },
      header: () => <Header title={"Completed On Time"} />,
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="text-sm">
            {item.completedOnTime !== undefined
              ? item.completedOnTime
                ? "Yes"
                : "No"
              : "N/A"}
          </div>
        );
      },
    },
    {
      accessorKey: "completedInContainer",
      meta: {
        label: "Completed In Container",
      },
      header: () => <Header title={"Completed In Container"} />,
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="text-sm">{item.completedInContainer || "N/A"}</div>
        );
      },
    },
    {
      accessorKey: "createdAt",
      meta: {
        label: "Created At",
      },
      header: ({ column }) =>
        isColumnSortable("createdAt", currentStatus) ? (
          <HeaderSortable column={column} title={"Created At"} />
        ) : (
          <Header title={"Created At"} />
        ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="text-sm">
            {firestoreToDateTime(item.createdAt)?.toLocaleString()}
          </div>
        );
      },
    },
  ];

  if (props) {
    col.push(createActionsColumn(props));
  }
  return col;
};

const layoutDefaults: TableLayoutState = {
  columnVisibility: {
    id: false,
    desiredUpdateTime: false,
    createdAt: false,
  },
};

export { customerUpdateColumns, layoutDefaults };
